import React, {useState} from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {Menu, MenuItem} from "@material-ui/core";
import {TextDialog} from "../dialogs";
import AddIcon from '@material-ui/icons/Add';
import {
  openDialogFolder,
  addPointCloudFiles,
  addCameraFiles,
  addAssetFolder,
  addOrthoFiles,
  addPlanarFiles,
  openDialogFile,
  openDialogMulti,
  imageOrthoFilter,
  imageCSVFilter,
  imagePlanarFilter,
  pointCloudFilter
} from '../asset-utils';
import {AssetFolder} from "./folder";
import {addFolder, selectDefaultFolder} from '../redux/folders-slice';
import {changeFolder, selectAssetsByFolderID} from '../redux/assets-slice';
import {useSelector, useDispatch} from 'react-redux';
import {DenseIcon} from '../dense-icon';
import path from 'path';
import { isStaticSite } from '../electron-modules';
import { useSnackbar } from 'notistack';
import { DenseDivider, SlimScrollbar } from '../toolbar';


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addItem: {
      paddingTop: "0px",
      paddingBottom: "0px"
    },
    folderList: {
      padding: "0px",
      overflow: "auto",
      flex: 1
    }
  }),
);

export const FolderList = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const showMessage = useSnackbar().enqueueSnackbar;

  const defaultFolder = useSelector(selectDefaultFolder);
  const defaultFolderHasAssets = useSelector(selectAssetsByFolderID(defaultFolder.id)).length > 0;
  const folders = useSelector(state => state.folders).filter(folder => !folder.isDefault || defaultFolderHasAssets);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [contextState, setContextState] = useState({
    mouseX: null,
    mouseY: null,
  });

  const handleSubmit = (name: string) => {
    dispatch(addFolder(name));
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleAddPoints = () => {
    handleClose();

    openDialogMulti(pointCloudFilter).then(paths => {
      if (paths.length === 0) {
        return;
      }

      addPointCloudFiles(dispatch, paths, defaultFolder.id);
    });
  };

  const handleAddOrtho = () => {
    handleClose();

    openDialogMulti(imageOrthoFilter).then(paths => {
      if (paths.length === 0) {
        return;
      }

      addOrthoFiles(showMessage, dispatch, paths, defaultFolder.id);
    });
  };

  const handleAddPanoramics = () => {
    handleClose();

    openDialogFile(imageCSVFilter).then(async assetPath => {
      if (!assetPath) {
        return;
      }

      const folderID = defaultFolder.id;
      addCameraFiles(showMessage, dispatch, [assetPath], folderID);
    });
  };

  const handleAddPlanars = () => {
    handleClose();

    openDialogFile(imagePlanarFilter).then(async assetPath => {
      if (!assetPath) {
        return;
      }

      const folderID = defaultFolder.id;
      addPlanarFiles(showMessage, dispatch, [assetPath], folderID);
    });
  };

  const handleAddFolder = () => {
    handleClose();

    openDialogFolder().then(({directoryPath, assetPaths}) => {
      assetPaths.then((paths) => {
        if (paths.length === 0) {
          return;
        }

        const folderName = path.basename(directoryPath);
        const action = dispatch(addFolder(folderName));
        const folderID = action.payload.id;
        addAssetFolder(showMessage, dispatch, paths, folderID);
      });
    });
  };

  const handleAssetMove = (assetID, folderID) => {
    dispatch(changeFolder({assetID, folderID}));
  };

  const handleClick = (event) => {
    setContextState({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleClose = () => {
    setContextState({
      mouseX: null,
      mouseY: null,
    });
  };

  const handleNewFolder = () => {
    handleClose();
    setDialogOpen(true);
  };

  const menuOpen = contextState.mouseY !== null;
  const anchorPosition = contextState.mouseY !== null && contextState.mouseX !== null
    ? { top: contextState.mouseY, left: contextState.mouseX }
    : undefined;

  return (
    <React.Fragment>
      {/* Add item button */}
      {(!isStaticSite) && (<React.Fragment>
        <ListItem
          button
          onClick={handleClick}
          className={classes.addItem}
        >
          <DenseIcon>
            <AddIcon fontSize="small"/>
          </DenseIcon>
          <ListItemText secondary="Add item"/>
        </ListItem>

        <DenseDivider variant="middle" spacing={10}/>
      </React.Fragment>)}

      <SlimScrollbar>
        <List className={classes.folderList}>
          {folders.map(folder =>
            <AssetFolder
              key={folder.id}
              folderID={folder.id}
              name={folder.name}
              handleAssetMove={handleAssetMove}
              viewer={props.viewer}
            />)}
        </List>
      </SlimScrollbar>

      <TextDialog
        open={dialogOpen}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        title="Create New Folder"
        prompt="Enter new folder name"
        default=''
        label="Folder Name"
        clear={true}
      />

      <Menu
        keepMounted
        open={menuOpen}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
      >
        <MenuItem onClick={handleNewFolder}>Create New Folder</MenuItem>
        <MenuItem onClick={handleAddPoints}>Add Point Cloud(s)</MenuItem>
        <MenuItem onClick={handleAddPanoramics}>Add Panoramic CSV/TXT</MenuItem>
        <MenuItem onClick={handleAddPlanars}>Add Planar Images</MenuItem>
        <MenuItem onClick={handleAddOrtho}>Add Orthomosaic(s)</MenuItem>
        <MenuItem onClick={handleAddFolder}>Add Folder Contents</MenuItem>
      </Menu>
    </React.Fragment>
  );
};
